import React, { useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useDebouncedCallback } from 'use-debounce';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import { setAlert } from 'handimap-react-lib/store/actions/alertActions';
import { useResponsive } from 'handimap-react-lib/hooks/useResponsive';
import { getGeolocationPermission } from 'handimap-react-lib/hooks/useGeoLocation';
import { useGeocoder } from 'handimap-react-lib/api/geocoderApi';

export const GeocoderInput = (props) => {
  const {
    placeholder,
    sx,
    inputValue,
    id,
    labelId,
    onComplete,
    describedBy = null,
  } = props;
  const { search } = useGeocoder();
  const { disable } = useSelector((state) => state.disable);
  const bbox = useSelector((state) => state.map.bbox);
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();

  const style = {
    defaultStyleAutocomplete: {
      display: 'flex',
      width: '100%',
      borderRadius: '0.5em',
      backgroundColor: '#FFFF',
      '.Mui-focused': {
        border: '1px solid white',
        borderTop: '2px solid white',
      },
      '& input::placeholder': {
        fontSize: isMobile ? '0.8em' : '1em',
        color: 'black',
        opacity: 0.6,
      },
      '& button': {
        visibility: 'visible',
      },
    },
  };

  /**
   * Accessibility infos: https://mui.com/material-ui/react-autocomplete/#accessibility
   */
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const renderLabelPoint = (feature, language) => {
    if (language === 'fr') {
      return feature.place_name_fr ?? feature.place_name;
    } else {
      return feature.place_name_en ?? feature.place_name;
    }
  };

  const onType = useDebouncedCallback(async (event, newValue) => {
    if (newValue !== null && newValue !== '') {
      const response = await search(newValue, bbox);
      if (response !== undefined) {
        if (response.geocoder && response.maptiler) {
          const redorderedResultsGeocoder = response.geocoder.results.sort(
            (elem1, elem2) => elem1.rank - elem2.rank,
          );
          const firstThree = redorderedResultsGeocoder
            .slice(0, 3)
            .map((elem) => ({
              id: elem.id,
              label: `${elem.name}`,
              geometry: {
                type: 'Point',
                coordinates: [elem.lon, elem.lat],
              },
            }));

          const remainingFromSecond = response.maptiler.features.map(
            (elem) => ({
              id: elem.id,
              label: renderLabelPoint(elem, i18n.language),
              geometry: elem.geometry,
            }),
          );

          // Combine the results
          const combinedResults = [...firstThree, ...remainingFromSecond];

          const options = combinedResults.map((feature) => {
            return {
              id: feature.id,
              label: feature.label,
              name: feature.label,
              lon: feature.geometry.coordinates[0],
              lat: feature.geometry.coordinates[1],
            };
          });
          setOptions(options);
        }

        if (response.geocoder && !response.maptiler) {
          const redorderedResultsGeocoder = response.geocoder.results.sort(
            (elem1, elem2) => elem1.rank - elem2.rank,
          );
          const firstTen = redorderedResultsGeocoder
            .slice(0, 10)
            .map((elem) => ({
              id: elem.id,
              label: `${elem.name}`,
              geometry: {
                type: 'Point',
                coordinates: [elem.lon, elem.lat],
              },
            }));
          const options = firstTen.map((feature) => {
            return {
              id: feature.id,
              label: feature.label,
              name: feature.label,
              lon: feature.geometry.coordinates[0],
              lat: feature.geometry.coordinates[1],
            };
          });
          setOptions(options);
        }
        if (!response.geocoder && response.maptiler) {
          return response.maptiler.features.map((elem) => ({
            id: elem.id,
            label: renderLabelPoint(elem, i18n.language),
            geometry: elem.geometry,
          }));
        }
      } else {
        setOptions([]);
      }
    }
  }, 500);

  const onSetUserLocation = () => {
    getGeolocationPermission().then((result) => {
      if (result.state === 'denied') {
        dispatch(
          setAlert(
            t('error.error-geolocation'),
            'error',
            t('error.label'),
            'alert',
          ),
        );
        onComplete('', '');
      } else {
        onComplete(t('common.search-position'), '');
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const value = {
              longitude: pos.coords.longitude,
              latitude: pos.coords.latitude,
            };
            if (value !== null && typeof value === 'object') {
              const center =
                value.longitude && value.latitude
                  ? [value.longitude, value.latitude]
                  : null;
              onComplete(t('common.location-mark'), center);
            } else {
              onComplete(null, '');
            }
          },
          (error) => {
            if (error.code == 1) {
              // Error: Access is denied!
              dispatch(
                setAlert(
                  t('error.error-geolocation'),
                  'error',
                  t('error.label'),
                  'alert',
                ),
              );
              onComplete('', '');
            } else if (error.code == 2) {
              // "Error: Position is unavailable!"
              onComplete(null, '');
            }
          },
        );
      }
    });
  };

  const onSetAddress = (value) => {
    if (value === null || typeof value !== 'object') {
      onComplete(null, '');
    } else {
      if (value.id === 'geo_location') {
        onSetUserLocation();
      } else {
        const center = value.lon && value.lat ? [value.lon, value.lat] : null;
        const label = value.name;
        onComplete(label, center);
      }
    }
  };

  return (
    <Autocomplete
      id={id}
      sx={sx ? sx : style.defaultStyleAutocomplete}
      disabled={disable}
      open={open}
      onOpen={() => setOpen(true)}
      renderOption={(props, option) => {
        if (option.id === 'geo_location') {
          return (
            <li
              {...props}
              key={option.id}
              style={{ display: 'flex', verticalAlign: 'middle' }}
            >
              <MyLocationIcon sx={{ marginRight: '.5em' }} />{' '}
              {t('common.location-mark')}
            </li>
          );
        } else {
          return (
            <li {...props} key={option.id}>
              {option.label}
            </li>
          );
        }
      }}
      onClose={() => setOpen(false)}
      filterOptions={(x) => x}
      options={[{ id: 'geo_location', label: '' }, ...options]}
      value={
        inputValue.value && !inputValue.label
          ? inputValue?.value.join(' / ')
          : inputValue.label
      }
      freeSolo
      autoComplete
      includeInputInList
      filterSelectedOptions
      selectOnFocus
      clearText={t('form.clear-button')}
      componentsProps={{
        clearIndicator: {
          tabIndex: 0,
          'aria-describedby': labelId,
        },
      }}
      onChange={(event, newValue) => {
        onSetAddress(newValue);
      }}
      onInputChange={onType}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              'aria-describedby': describedBy,
              width: '100%',
            }}
            placeholder={placeholder}
            size={isMobile ? 'small' : 'medium'}
          />
        );
      }}
    />
  );
};
